import { needsCsrf, readCsrfCookie, CSRF_HEADER } from './csrf';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiRequestInit = RequestInit & { skipAuthRedirect?: boolean };

let csrfBootstrap: Promise<string | null> | null = null;

async function ensureCsrfToken(): Promise<string | null> {
  const existing = readCsrfCookie();
  if (existing) return existing;
  if (!csrfBootstrap) {
    csrfBootstrap = fetch('/api/auth/csrf', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return null;
        const data = (await r.json()) as { csrfToken?: string };
        return data.csrfToken ?? readCsrfCookie();
      })
      .finally(() => {
        csrfBootstrap = null;
      });
  }
  return csrfBootstrap;
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (needsCsrf(method)) {
    const token = (await ensureCsrfToken()) ?? readCsrfCookie();
    if (token) headers.set(CSRF_HEADER, token);
  }

  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && !init.skipAuthRedirect) {
    if (!path.includes('/auth/login') && !path.includes('/auth/csrf')) {
      window.location.assign(
        `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`,
      );
    }
  }

  if (res.status === 403) {
    throw new ApiError(403, 'Geen toegang — controleer 2FA of rechten.', 'FORBIDDEN');
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const err = payload as { error?: string; code?: string; details?: unknown } | null;
    throw new ApiError(res.status, err?.error ?? res.statusText, err?.code, err?.details);
  }

  return payload as T;
}

export async function refreshCsrf(): Promise<string | null> {
  const data = await apiFetch<{ csrfToken: string }>('/api/auth/csrf', {
    skipAuthRedirect: true,
  });
  return data.csrfToken ?? readCsrfCookie();
}
